import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Button } from "src/components/button/Button";
import type { Merge } from "src/models/utility";
import { SelectDropdownParts } from "..";
import type { SelectOption } from "../selectOption/selectOption";
import { PopoverParts } from "../../popover";
import styles from "./SelectDropdown.root.module.scss";
import { Icon } from "src/components/icon/Icon";

const SelectDropdownContext = createContext<SelectDropdownRoot.Context>({
	selectedItem: {} as SelectOption.Props,
	onSelectChange: () => {},
});

export const useSelectDropdownContext = () => {
	const context = useContext(SelectDropdownContext);
	if (!context) {
		throw new Error("useSelectDropdownContext must be used within a SelectDropdownRoot");
	}
	return context;
};

export namespace SelectDropdownRoot {
	export type ElementName = "div";
	export type ElementType = HTMLDivElement;
	export type Context = {
		selectedItem?: SelectOption.Props;
		onSelectChange: (item: SelectOption.Props) => void;
	};
	export type Props = Merge<
		Omit<PopoverParts.Root.Props, "onChange" | "open" | "onOpenChange">,
		{
			placeholderText?: string;
			ref?: React.Ref<HTMLButtonElement>;
			size?: Button.Props["size"];
			preSelected?: number | SelectOption.Props;
			options: SelectOption.Props[];
			onChange?: (item: SelectOption.Props) => void;
			content?: PopoverParts.Content.Props;
			trigger?: PopoverParts.Button.Props & {
				ignoreLabel?: boolean;
				useChevrons?: boolean;
			};
		}
	>;
}

export const SelectDropdownRoot = (props: SelectDropdownRoot.Props) => {
	const {
		placeholderText,
		preSelected,
		options,
		trigger = {
			variant: "bordered",
			color: "neutral",
			useChevrons: true,
			ignoreLabel: false,
			children: undefined,
			paddingRule: "text-only"
		},
		content,
		onChange,
		...rest
	} = props;

	const _getPreselectedItem = useCallback(() => {
		if (preSelected !== undefined && options?.length) {
			if (typeof preSelected === "number") {
				const found = options[preSelected];
				if (found) return found;
			} else {
				return preSelected;
			}
		}
		if (!preSelected && options?.length) {
			return options[0];
		}
		return options[0];
	}, [preSelected, options]);

	const [selectedItem, setSelectedItem] = useState<SelectOption.Props>();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!selectedItem) {
			setSelectedItem(_getPreselectedItem());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedItem, _getPreselectedItem]);

	useEffect(() => {
		if (selectedItem) {
			setOpen(false);
		}
	}, [selectedItem]);

	const onSelectChange = (item: SelectOption.Props) => {
		setSelectedItem(item);
		onChange?.(item);
	};

	const context = useMemo(
		() => ({
			selectedItem,
			onSelectChange,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedItem, onSelectChange],
	);

	const _getTriggerProps = () => {
		const { useChevrons: _, ignoreLabel: __, ...triggerProps } = trigger;
		return triggerProps;
	};

	const _renderItems = () => {
		return options?.map((option: SelectOption.Props) => {
			const { children, ...optionRest } = option;
			return (
				<SelectDropdownParts.Item
					key={option.id || option.text}
					color="neutral"
					variant="ghost"
					{...optionRest}
				>
					{children}
				</SelectDropdownParts.Item>
			);
		});
	};

	return (
		<SelectDropdownContext.Provider value={context}>
			<PopoverParts.Root open={open} onOpenChange={setOpen} {...rest}>
			<PopoverParts.Button
				className={styles["trigger"]}
				text={trigger.ignoreLabel ? undefined : (selectedItem?.text ?? placeholderText)}
				leftIcon={selectedItem?.leftIcon}
				rightIcon={trigger.useChevrons ? (open ? <Icon name="SmallChevronUp" /> : <Icon name="SmallChevronDown" />) : selectedItem?.rightIcon}
				id={selectedItem?.id}
				{...(_getTriggerProps())}
			>
				{(trigger.ignoreLabel ? undefined : selectedItem?.children) || trigger.children}
			</PopoverParts.Button>
				<PopoverParts.Content>
					{_renderItems()}	
				</PopoverParts.Content>
			</PopoverParts.Root>
		</SelectDropdownContext.Provider>
	);
};
